<template>
  <v-container>
    <v-row>
      <v-col cols="11">
        <v-text-field
          v-model="scanName"
          :error-messages="scanNameErrors"
          :disabled="uploading"
          prepend-inner-icon="mdi-label"
          label="Your scan's label"
          hint="An identifying label for your scan (e.g. &quot;July 7 neck MRI&quot;)"
        />
      </v-col>
      <v-col cols="1">
        <v-btn
          v-if="!uploading"
          :disabled="!readyToUpload"
          color="primary"
          @click="startUpload"
        >
          Upload
        </v-btn>
        <v-btn
          v-else
          :loading="cancel"
          text
          @click="cancel = true"
        >
          Cancel
        </v-btn>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <v-card
          class="fill-height"
          flat
        >
          <folder-dropzone
            v-if="!files.length"
            @change="change"
          />
          <FileUploadList
            v-model="files"
            :max-show="100"
            :current-index="currentIndex"
            :uploading="uploading"
          />
          <v-progress-linear
            v-if="uploading"
            :value="totalProgressPercent"
            height="20"
          />
          <v-alert
            v-if="errorMessage"
            type="error"
          >
            {{ errorMessage }}
            <v-btn
              v-if="!uploading"
              dark
              outlined
              @click="startUpload"
            >
              Resume upload
            </v-btn>
          </v-alert>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import FileUploadList from '@/components/FileUploadList.vue';
import FolderDropzone from '@/components/FolderDropzone.vue';
import http from '@/http';

class FileUploader {
  constructor(file) {
    this.file = file;
    this.status = 'pending';
    this.name = this.file.webkitRelativePath;
    this.progress = {
      total: file.size,
      current: 0,
      indeterminate: false,
    };
  }

  async start(scan) {
    this.status = 'uploading';
    const formData = new FormData();
    formData.append('file', this.file);
    formData.append('scan', scan);

    await http.request({
      url: '/scan-file/',
      method: 'post',
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (event) => this.uploadProgress(event),
    });
    this.status = 'done';
  }

  uploadProgress(event) {
    this.progress.current = event.loaded;
  }
}

export default {
  components: {
    FileUploadList,
    FolderDropzone,
  },
  data() {
    return {
      files: [],
      errorMessage: '',
      statusMessage: '',
      uploading: false,
      totalProgressPercent: 0,
      currentIndex: 0,
      scanName: '',
      scanNameErrors: [],
      cancel: false,
    };
  },
  computed: {
    readyToUpload() {
      return this.files.length > 0 && this.scanName.length > 0;
    },
  },
  methods: {
    async startUpload() {
      this.uploading = true;
      const scan = await this.createScan(this.scanName);
      for (let i = 0; i < this.files.length; i += 1) {
        this.currentIndex = i;
        const upload = this.files[i];
        await upload.start(scan); // eslint-disable-line no-await-in-loop
        this.totalProgressPercent = 100 * ((i + 1) / this.files.length);

        if (this.cancel) {
          return this.cancelUploadNow(scan);
        }
      }

      this.files = [];
      this.uploading = false;
      this.totalProgressPercent = 0;
      this.currentIndex = 0;
      this.$emit('complete');
      return true;
    },
    async createScan(name) {
      try {
        const resp = await http.request({
          url: '/scan/',
          method: 'post',
          data: {
            name,
          },
        });
        return resp.data.id;
      } catch (error) {
        this.errorMessage = 'Could not create scan';
        throw error;
      }
    },
    change(event) {
      this.files = event.map((file) => new FileUploader(file))
        .sort((a, b) => (a.name < b.name ? -1 : 1));
    },
    async cancelUploadNow(scan) {
      await http.request({
        url: `/scan/${scan}/`,
        method: 'delete',
      });
      this.files = [];
      this.cancel = false;
      this.uploading = false;
    },
  },
};
</script>
